import { boardToEmoji, type GuessEntry, type RaceRound, type TimedScore } from "@mk/shared";
import { messages } from "../../i18n/index.ts";
import { gameEdition, upperLabel } from "../../lib/edition.ts";

/**
 * Paylaşılan sonucun metni.
 *
 * Emoji tablo `boardToEmoji` ile üretiliyor — harf içermiyor, yani günlük
 * kelimeyi henüz oynamamış birine cevabı sızdırmıyor. Görselde harf gösterme
 * kararı ayrı ve oyuncunun elinde (bkz. shareCard).
 */

export type ShareResult =
  | {
      mode: "daily";
      /** "8 Ağustos" gibi okunur gün. */
      dayLabel: string;
      solved: boolean;
      attempts: number;
      /**
       * Oyun bittikten SONRAKİ seri. Bilemeyende sunucu seriyi sıfırladığı için
       * bu 0 olur; kaç günlük serinin bittiğini `brokenStreak` taşır.
       */
      streak: number;
      /** Bilemeyende: bugünden önce kaç günlük seri vardı. Bilinmiyorsa null. */
      brokenStreak: number | null;
    }
  | { mode: "practice"; solved: boolean; attempts: number }
  | {
      mode: "timed";
      wordsSolved: number;
      solvedAttempts: number;
      newRecord: boolean;
      /** Tamamlanan kelimeler sırayla; cevaplar paylaşıma girmez. */
      rounds: readonly { solved: boolean; attempts: number }[];
    }
  | {
      mode: "duel";
      opponentName: string;
      won: boolean;
      draw: boolean;
      yourAttempts: number;
      opponentAttempts: number;
      solved: boolean;
    };

export function shareUrl(): string {
  if (typeof window !== "undefined") return window.location.origin;
  return gameEdition.id === "en"
    ? "https://magnificentword.com"
    : "https://muhtesemkelime.com";
}

/** Görselin ve metnin üstündeki küçük satır. */
export function shareEyebrow(result: ShareResult): string {
  switch (result.mode) {
    case "daily":
      return messages.shareText.daily(upperLabel(result.dayLabel));
    case "practice":
      return messages.shareText.solo;
    case "timed":
      return messages.shareText.timed;
    case "duel":
      return messages.shareText.duel;
  }
}

/**
 * Sonucun tek cümlelik anlatımı.
 *
 * Sayılara Türkçe ek getirmiyoruz: "3'e 5" gibi biçimler yerine tire kullanmak
 * hem okunur hem de ek uyumu hatasına düşmez.
 */
export function shareMessage(result: ShareResult): string {
  if (result.mode === "daily") {
    if (result.solved) {
      const attempt = messages.shareText.dailySolved(result.attempts);
      if (result.streak >= 2) return messages.shareText.streakUp(attempt, result.streak);
      if (result.streak === 1) return messages.shareText.streakRestart(attempt);
      return `${attempt}.`;
    }
    if (result.brokenStreak && result.brokenStreak > 0) {
      return messages.shareText.dailyMissedBroken(result.brokenStreak);
    }
    return messages.shareText.dailyMissed;
  }

  if (result.mode === "timed") {
    if (result.wordsSolved === 0) return messages.shareText.timedNone;
    return result.newRecord
      ? messages.shareText.timedRecord(result.wordsSolved, result.solvedAttempts)
      : messages.shareText.timedSolved(result.wordsSolved, result.solvedAttempts);
  }

  if (result.mode === "practice") {
    return result.solved
      ? messages.shareText.soloSolved(result.attempts)
      : messages.shareText.soloMissed;
  }

  if (result.draw) {
    /*
     * Aynı tahmin sayısı artık beraberlik (süre kazananı belirlemiyor), yani
     * bu durum sık görülecek. Sayıyı yazmazsak paylaşımda "kaç tahminde"
     * bilgisi tamamen kayboluyordu.
     */
    return result.solved && result.yourAttempts === result.opponentAttempts
      ? messages.shareText.duelDrawSolved(result.opponentName, result.yourAttempts)
      : messages.shareText.duelDraw(result.opponentName);
  }
  if (result.won) {
    return result.solved
      ? messages.shareText.duelWonScore(result.opponentName, result.yourAttempts, result.opponentAttempts)
      : messages.shareText.duelWon(result.opponentName);
  }
  return messages.shareText.duelLost(result.opponentName, result.opponentAttempts, result.yourAttempts);
}

/**
 * Panoya kopyalanan / metin olarak paylaşılan tam sonuç.
 * Görsel paylaşılamayan yerlerde (X, bazı tarayıcılar) tek başına da anlamlı.
 */
export function shareText(result: ShareResult, rows: readonly GuessEntry[]): string {
  const board = result.mode === "timed"
    ? timedEmoji(result.rounds)
    : boardToEmoji(rows.map((row) => row.pattern));
  return [
    `${gameEdition.productName} · ${shareEyebrow(result)}`,
    shareMessage(result),
    "",
    board,
    "",
    shareUrl(),
  ].join("\n");
}

const TIMED_EMOJI_PER_LINE = 10;

/** 3 dakika koşusu: kelime başına bir kare, bulunan yeşil, kaçan kırmızı. */
export function timedEmoji(rounds: readonly { solved: boolean }[]): string {
  const cells = rounds.map((round) => (round.solved ? "🟩" : "🟥"));
  const lines: string[] = [];
  for (let index = 0; index < cells.length; index += TIMED_EMOJI_PER_LINE) {
    lines.push(cells.slice(index, index + TIMED_EMOJI_PER_LINE).join(""));
  }
  return lines.join("\n");
}

/** Bitmiş bir 3 dakika koşusunu paylaşım sonucuna çevirir (sonuç ekranı ve geçmiş). */
export function timedShareResult(
  run: TimedScore & { rounds: readonly RaceRound[] },
  newRecord: boolean,
): Extract<ShareResult, { mode: "timed" }> {
  return {
    mode: "timed",
    wordsSolved: run.wordsSolved,
    solvedAttempts: run.solvedAttempts,
    newRecord,
    rounds: run.rounds.map((round) => ({ solved: round.solved, attempts: round.guesses.length })),
  };
}

/** Görselde tahta yerine çizilen özet: büyük sayı ve kelime başına bir kare. */
export function timedShareSummary(result: Extract<ShareResult, { mode: "timed" }>) {
  return {
    value: String(result.wordsSolved),
    caption: result.newRecord
      ? `${messages.shareText.timedWords} · ${messages.solo.timedNewRecord}`
      : messages.shareText.timedWords,
    chips: result.rounds.map((round) => ({
      solved: round.solved,
      label: round.solved ? String(round.attempts) : "×",
    })),
  };
}

/** Paylaşım penceresinin başlığı. */
export function shareTitle(result: ShareResult): string {
  return `${gameEdition.productName} · ${shareEyebrow(result)}`;
}
